// Govard CRM — web shell root.
//
// Wires the top-level providers that every route relies on:
//   - <I18nProvider>       → ru catalog (shared + crm namespaces).
//   - <AnalyticsProvider>  → stdout-only client in Phase 1 (ADR-0002).
//   - <FlagsProvider>      → env-backed feature flags (pro-mode, ai-chat).
//   - <RouterProvider>     → v7 router, see ./router.tsx.
//
// The analytics + flags ctx are memoised at module scope so their identity
// stays stable across re-renders (both providers compare by reference).

import { useMemo, type ReactElement } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';

import { AnalyticsProvider } from '@govard/analytics';
import { FlagsProvider } from '@govard/feature-flags/react';
import { I18nProvider } from '@govard/i18n-core/react';

import { createAnalytics } from './analytics/index.js';
import { buildFlagsCtx, flagsRegistry } from './feature-flags/index.js';
import { i18n } from './i18n/index.js';
import { router } from './router.js';
import { useScopeStore } from './shared/scope/useCurrentScope.js';

// Single instance for the lifetime of the tab. Analytics buffers + dedupe
// state MUST NOT reset on every render.
const analytics = createAnalytics();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

export function App(): ReactElement {
  const scope = useScopeStore((state) => state.scope);
  const user = useScopeStore((state) => state.user);
  const workspaceSettings = useScopeStore((state) => state.workspaceSettings);
  const flagsCtx = useMemo(
    () => buildFlagsCtx({ scope, user, workspaceSettings }),
    [scope, user, workspaceSettings],
  );

  return (
    <I18nProvider i18n={i18n}>
      <AnalyticsProvider client={analytics}>
        <FlagsProvider registry={flagsRegistry} ctx={flagsCtx}>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </FlagsProvider>
      </AnalyticsProvider>
    </I18nProvider>
  );
}
